/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { FormattedDate, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router'
import PageTitle from '../../../../client/js/components/PageTitle'
import { DateTime, DurationUnit } from 'luxon'
import { useQuery } from '@tanstack/react-query'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { Button, Card, Container, Divider, SegmentedControl, Stack, Text } from '@mantine/core'
import CustomerReport from './CustomerReport'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { useEffect, useMemo } from 'react'
import Toolbar from '../../../../client/js/components/Toolbar'
import { Link } from 'react-router-dom'
import { stringify } from 'qs'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons/faCalendarDay'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import { fetchCustomerReportData } from '../../lib/rest/customerAPIClient'
import { parseNumber } from '@jalik/form-parser'
import Page from '../../../../client/js/components/Page'
import { useRouteLoaderData } from '../../../../client/js/components/RouteLoaderData'
import CustomerBreadcrumb from './CustomerBreadcrumb'

function CustomerReportPage () {
  const intl = useIntl()
  const params = useParams<{ id: string }>()
  const customerId = parseInt(params.id, 10)
  const customer = useRouteLoaderData<Customer>()

  const navigate = useNavigate()
  const year = useParsedQueryParam('year', parseNumber)
  const month = useParsedQueryParam('month', parseNumber)
  const unit: DurationUnit = month > 0 ? 'month' : 'year'

  const currentDt = DateTime.fromObject({ year, month })
  const prevDt = currentDt.minus({ [unit]: 1 })
  const nextDt = currentDt.plus({ [unit]: 1 })

  const query = useMemo(() => ({
    year, month
  }), [month, year])

  const reportQuery = useQuery({
    enabled: year > 0,
    queryKey: ['customerReportData', customerId, query],
    queryFn: () => fetchCustomerReportData(customerId, query),
    refetchOnWindowFocus: true
  })

  useEffect(() => {
    // Display current month.
    if (year == null || year <= 0) {
      const dt = DateTime.now()
      navigate(
        { search: stringify({ year: dt.year, month: dt.month }) },
        { replace: true }
      )
    }
  }, [navigate, year])

  const title = intl.formatMessage({
    id: 'report_$period',
    defaultMessage: 'Rapport {period}'
  }, {
    period: intl.formatDate(currentDt.toISO(), {
      year: 'numeric',
      month: unit === 'month' ? 'numeric' : undefined
    })
  })

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <CustomerBreadcrumb
          customer={customer}
          title={title}
        />

        <Card shadow="sm">
          <Stack>
            <Toolbar>
              <Button
                disabled={currentDt.hasSame(DateTime.now(), unit)}
                component={Link}
                to={`?${stringify({
                  year: DateTime.now().year,
                  month: unit === 'month' ? DateTime.now().month : undefined
                })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faCalendarDay}
                />
              </Button>
              <Button
                component={Link}
                to={`?${stringify({
                  year: prevDt.year,
                  month: unit === 'month' ? prevDt.month : undefined
                })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronLeft}
                />
              </Button>
              <Text tt="uppercase" fw="bold">
                <FormattedDate
                  value={currentDt.toJSDate()}
                  year="numeric"
                  month={unit === 'month' ? 'long' : undefined}
                />
              </Text>
              <Button
                component={Link}
                to={`?${stringify({
                  year: nextDt.year,
                  month: unit === 'month' ? nextDt.month : undefined
                })}`}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronRight}
                />
              </Button>
              <Button
                disabled={reportQuery.isFetching}
                loading={reportQuery.isFetching}
                onClick={() => {
                  reportQuery.refetch()
                }}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faRefresh}
                />
              </Button>
              <SegmentedControl
                data={[
                  {
                    label: intl.formatMessage({
                      id: 'monthly',
                      defaultMessage: 'Mensuel'
                    }),
                    value: 'month'
                  },
                  {
                    label: intl.formatMessage({
                      id: 'yearly',
                      defaultMessage: 'Annuel'
                    }),
                    value: 'year'
                  }
                ]}
                onChange={(v) => {
                  navigate({
                    search: stringify(v === 'month'
                      ? { year, month: 1 }
                      : { year })
                  })
                }}
                value={unit}
              />
            </Toolbar>

            <Divider />

            <LoadingHelper
              query={reportQuery}
              render={() => (
                <CustomerReport data={reportQuery.data} />
              )}
            />
          </Stack>
        </Card>
      </Container>
    </Page>
  )
}

export default CustomerReportPage
